import { memo, useCallback } from 'react';
import * as RadixAlertDialog from '@radix-ui/react-alert-dialog';
import { Button } from '../Button.js';
import styles from '../ConfirmDialog/ConfirmDialog.module.css';

export type AlertDialogVariant = 'success' | 'error' | 'info';

/**
 * Blocking acknowledge dialog (D5). FA-06 audit: no isBusy — single synchronous
 * acknowledge only; Escape dismiss allowed (no async pending path).
 */
export interface AlertDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description?: React.ReactNode;
  readonly variant?: AlertDialogVariant;
  readonly actionLabel: string;
  readonly onAction: () => void;
  readonly testId?: string;
  readonly messageTestId?: string;
  readonly actionTestId?: string;
}

function messageClassName(variant: AlertDialogVariant): string {
  if (variant === 'success') {
    return 'rounded-lg border border-[color-mix(in_srgb,var(--color-an-success)_35%,var(--color-an-border))] bg-[var(--color-success-light)] px-3 py-2 text-sm text-[var(--color-an-text)]';
  }
  if (variant === 'error') {
    return 'rounded-lg border border-[color-mix(in_srgb,var(--color-an-danger)_35%,var(--color-an-border))] bg-[var(--color-error-light)] px-3 py-2 text-sm text-[var(--color-an-text)]';
  }
  return 'rounded-lg border border-[color-mix(in_srgb,var(--color-an-primary)_35%,var(--color-an-border))] bg-[var(--color-an-primary-soft)] px-3 py-2 text-sm text-[var(--color-an-text)]';
}

export const AlertDialog = memo<AlertDialogProps>(({
  open,
  onOpenChange,
  title,
  description,
  variant = 'info',
  actionLabel,
  onAction,
  testId = 'alert-dialog',
  messageTestId,
  actionTestId,
}) => {
  const handleAction = useCallback((): void => {
    onAction();
  }, [onAction]);

  const resolvedMessageTestId = messageTestId ?? `${testId}-message`;
  const resolvedActionTestId = actionTestId ?? `${testId}-action`;

  return (
    <RadixAlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixAlertDialog.Portal>
        <RadixAlertDialog.Overlay className={styles.overlay} data-testid={`${testId}-overlay`} />
        <RadixAlertDialog.Content
          className={styles.content}
          data-testid={testId}
          aria-describedby={description !== undefined ? `${testId}-description` : undefined}
        >
          <header className={styles.header}>
            <RadixAlertDialog.Title className={styles.title}>{title}</RadixAlertDialog.Title>
          </header>
          <div className={styles.body}>
            {description !== undefined ? (
              <RadixAlertDialog.Description
                id={`${testId}-description`}
                className={messageClassName(variant)}
                data-testid={resolvedMessageTestId}
                data-variant={variant}
                asChild={typeof description !== 'string'}
              >
                {typeof description === 'string' ? description : <div>{description}</div>}
              </RadixAlertDialog.Description>
            ) : null}
          </div>
          <div className={styles.footer}>
            <RadixAlertDialog.Action asChild>
              <Button
                type="button"
                intent="primary"
                size="md"
                onClick={handleAction}
                data-testid={resolvedActionTestId}
              >
                {actionLabel}
              </Button>
            </RadixAlertDialog.Action>
          </div>
        </RadixAlertDialog.Content>
      </RadixAlertDialog.Portal>
    </RadixAlertDialog.Root>
  );
});

AlertDialog.displayName = 'AlertDialog';
